import React from "react";
import "./marketing.css";
import heroBg from "./hero-bg.jpg";
import dashboardImage from "./engine-dashboard.jpg";
import problemImage from "./problem-image.png";
import caseStudyImage from "./case-study-img.jpg";
import ctaImage from "./CTA-img.jpg";

const painPoints = [
  {
    title: "Leads Come In",
    text: "Inquiries flow in from ads, portals, and referrals, creating the illusion of momentum.",
  },
  {
    title: "Follow-Ups Break Down",
    text: "No structured system means hot leads go cold. Manual follow-up is inconsistent at best.",
  },
  {
    title: "Zero Visibility on Performance",
    text: "Without a proper pipeline, you can't tell where deals are dying in the process.",
  },
];

const researchStats = [
  {
    value: "21x",
    title: "More Likely to Qualify a Lead",
    text: "Responding in under 5 minutes can make a lead 21x more likely to qualify.",
  },
  {
    value: "78%",
    title: "Buyers Choose the First Responder",
    text: "78% of homebuyers work with the first agent who responds to their inquiry.",
  },
  {
    value: "67%",
    title: "Conversion Lift with AI CRM",
    text: "AI-powered CRM systems can increase lead-to-close conversion by up to 67%.",
  },
  {
    value: "87%",
    title: "Deals Lost to Poor Follow-Up",
    text: "Most real estate deals are lost due to delayed or inadequate follow-up.",
  },
];

const costStats = [
  {
    value: "87%",
    title: "Deals Lost to Poor Follow-Up",
    text: "Nearly 9 in 10 real estate deals are lost because of bad lead management.",
  },
  {
    value: "15+ hrs",
    title: "Average Lead Response Time",
    text: "Average response time exceeds 15 hours, while the best practice is under 5 minutes.",
  },
  {
    value: "67%",
    title: "Conversion Lift with AI CRM",
    text: "Developers using structured CRM automation see significantly higher conversion rates.",
  },
];

const builderSystem = [
  {
    no: "01",
    title: "Lead Generation",
    text: "Engineered demand through precision-targeted paid media across Meta and Google ecosystems.",
  },
  {
    no: "02",
    title: "Conversion System",
    text: "CRM infrastructure, automated follow-ups, and sales pipeline management that closes deals faster.",
  },
  {
    no: "03",
    title: "Authority Layer",
    text: "Strategic content and brand positioning that builds trust and makes you the obvious choice.",
  },
];

const demandItems = [
  {
    title: "Meta & Google Ecosystem",
    text: "Full-funnel ad architecture across both platforms — from awareness to conversion — with messaging calibrated to each stage of the buyer journey.",
  },
  {
    title: "Advanced Audience Targeting",
    text: "Custom audiences, lookalike modeling, and behavioral targeting that reaches qualified buyers — not just browsers.",
  },
  {
    title: "Continuous Optimisation",
    text: "Weekly creative testing, bid strategy refinement, and performance analysis ensure your cost-per-lead decreases as the system matures.",
  },
];

const trustCards = [
  {
    title: "Strategic Content Creation",
    text: "Project walkthroughs, developer interviews, market insight videos, and lifestyle content that educates and attracts the right buyers at scale.",
  },
  {
    title: "Brand Positioning",
    text: "We define and communicate your unique developer narrative — making you the premium, trustworthy choice against competitors spending the same budget.",
  },
  {
    title: "Social Proof Architecture",
    text: "Systematically collecting and distributing testimonials, project milestones, and buyer success stories that accelerate trust and reduce sales friction.",
  },
];

const caseStudyItems = [
  {
    title: "No System in Place",
    text: "Leads were collected in spreadsheets. Follow-ups depended on individual salespeople's motivation — not a structured process.",
  },
  {
    title: "Structured Process Installed",
    text: "We implemented a CRM, automated follow-up sequences, and a weekly performance review cadence tied to real pipeline data.",
  },
  {
    title: "Conversion Rate Climbed",
    text: "Within 90 days, lead-to-visit conversion improved significantly — and the sales team closed more deals from the same lead volume.",
  },
];

const growthPlanCards = [
  {
    title: "Custom System Setup",
    text: "Full architecture designed for your specific projects, team structure, and buyer profile. No templates. No shortcuts. Built to perform from day one.",
  },
  {
    title: "Performance-Driven Execution",
    text: "Our team operates as a dedicated extension of your business — with clear KPIs, weekly reporting, and full accountability for pipeline outcomes.",
  },
  {
    title: "Scalable Infrastructure",
    text: "The systems we build don't just work for today's projects — they grow with you as you expand your portfolio, team, and market reach.",
  },
];

const dailySystemCards = [
  {
    title: "Week 1–2",
    text: "Strategy deep-dive, full funnel audit, system architecture and tech stack confirmed.",
  },
  {
    title: "Week 3–4",
    text: "CRM live, ads launched, content foundation deployed, automation workflows active.",
  },
  {
    title: "Day 30+",
    text: "First performance review, optimisations begin, pipeline visibility in place. Your system is running.",
  },
];

const processSteps = [
  {
    no: "01",
    title: "Strategy",
    text: "Deep audit of funnel, audience, and current execution. We map the system architecture first.",
  },
  {
    no: "02",
    title: "Setup",
    text: "CRM configuration, ad account structure, content foundation, and automated workflows.",
  },
  {
    no: "03",
    title: "Optimise",
    text: "Weekly refinement, A/B testing, and data-led feedback loops to improve conversion.",
  },
  {
    no: "04",
    title: "Scale",
    text: "Expand into new projects and markets on top of a system proven to perform.",
  },
];

const testimonials = [
  {
    quote:
      "Landtrack’s Marketing Service helped us move beyond basic content and actually pull buyer attention. Their structured approach to content, funnels made a visible difference in our lead quality and engagement.",
    by: "Raheel Rinch, MD, Aman Builders",
  },
  {
    quote:
      "Kudos to the team, landtrack.pk! Their service brought consistency, clarity, and real engagement for our brand.",
    by: "Ahsan Jabbar, CEO AJ Ventures",
  },
  {
    quote:
      "The entire landtrack team deserves our gratitude for their efforts. Keep up the good work!",
    by: "Fayyaz Ilyas, CEO Falaknaz Builders",
  },
  {
    quote:
      "They helped me build my personal brand across various social platforms, and their SEO game is very strong. I highly recommend their services to anyone looking to establish a strong online presence.",
    by: "Mohamad Itani, CEO MI Real Estate",
  },
  {
    quote:
      "Landtrack.pk property technology ke is aham mawzoo ko lead ker raha hai. I wish them all the best in their mission to transform the real estate industry.",
    by: "Rizwan Adhia, CEO, Country Builders",
  },
];

const MarketingPage = () => {
  const scrollToSection = (id) => {
    const target = document.getElementById(id);
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="marketing-page-v2">
      <section className="mkt-hero" style={{ backgroundImage: `linear-gradient(rgba(11,14,18,0.65), rgba(11,14,18,0.75)), url(${heroBg})` }}>
        <div className="mkt-wrap">
          <p className="mkt-kicker">LANDTRACK.PK</p>
          <h1>Build a Predictable Real Estate Sales System</h1>
          <p>
            Stop relying on random leads. Start building consistent, compounding revenue every single month.
          </p>
          <div className="mkt-btn-row">
            <button onClick={() => scrollToSection("sales-system")}>Start Your System</button>
            <button className="mkt-btn-outline" onClick={() => scrollToSection("how-it-works")}>See How It Works</button>
          </div>
        </div>
      </section>

      <section className="mkt-section">
        <div className="mkt-wrap">
          <h2>Most Real Estate Marketing Looks Busy... But Doesn't Convert.</h2>
          <p className="mkt-copy">
            Campaigns are running. Phones are ringing. The team is hustling. And yet at the end of the month, the numbers don't add up.
          </p>
          <div className="mkt-grid mkt-grid-3">
            {painPoints.map((item) => (
              <article className="mkt-card" key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
          <p className="mkt-callout">Activity + revenue. A full pipeline means nothing without a system to convert it.</p>
        </div>
      </section>

      <section className="mkt-section">
        <div className="mkt-wrap">
          <h2>It's Not an Ads Problem. It's a System Problem.</h2>
          <p className="mkt-copy">
            Most developers assume the fix is more budget, better creatives or a new agency. But the real issue isnt at the top of the funnel.
            <br />
            It's everything that happens after the lead comes in.
          </p>
          <img src={problemImage} alt="Sales system problem illustration" className="mkt-problem-image" />
          <p className="mkt-copy">
            More budget won't fix a broken sales engine. It only amplifies the chaos and the cost.
          </p>
          <p className="mkt-callout">87% of deals are lost due to poor follow-up, not bad leads.</p>
        </div>
      </section>

      <section className="mkt-section">
        <div className="mkt-wrap">
          <h2>The Data Doesn't Lie</h2>
          <p className="mkt-copy">The gap between top-performing operations and everyone else is mostly systems.</p>
          <div className="mkt-grid mkt-grid-4">
            {researchStats.map((item) => (
              <article className="mkt-stat" key={item.title}>
                <h3>{item.value}</h3>
                <h4>{item.title}</h4>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
          <p className="mkt-callout">These aren't edge cases. They're industry averages your competitors are already exploiting.</p>
        </div>
      </section>

      <section className="mkt-section">
        <div className="mkt-wrap">
          <h2>What This Is Costing You</h2>
          <div className="mkt-grid mkt-grid-3">
            {costStats.map((item) => (
              <article className="mkt-stat" key={item.title}>
                <h3>{item.value}</h3>
                <h4>{item.title}</h4>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
          <p className="mkt-callout">A 10% conversion lift can mean crores in added revenue without increasing ad spend.</p>
        </div>
      </section>

      <section className="mkt-section" id="sales-system">
        <div className="mkt-wrap">
          <h2>We Build Your Sales Engine</h2>
          <p className="mkt-copy">
            Landtrack.pk isn't an ad agency. We design, build, and operate end-to-end sales systems for real estate developers.
          </p>
          <div className="mkt-grid mkt-grid-3 mkt-system-grid">
            {builderSystem.map((item) => (
              <article className="mkt-light-card" key={item.no}>
                <h3>{item.no} — {item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mkt-section">
        <div className="mkt-wrap mkt-split mkt-demand-split">
          <div className="mkt-demand-copy">
            <p className="mkt-kicker mkt-section-kicker">COMPONENT 01</p>
            <h2>We Don't Just Run Ads. We Engineer Demand.</h2>
            <p className="mkt-copy">
              Most agencies launch campaigns and hope for results. We build a demand engine — a precisely targeted,
              continuously optimized media system designed to put your project in front of buyers who are ready to act.
            </p>
            <div className="mkt-demand-list">
              {demandItems.map((item) => (
                <article className="mkt-demand-item" key={item.title}>
                  <div className="mkt-demand-icon" aria-hidden="true" />
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
          <img src={dashboardImage} alt="Analytics dashboard" className="mkt-side-image mkt-demand-image" />
        </div>
      </section>

      <section className="mkt-section">
        <div className="mkt-wrap mkt-close-section">
          <h2>Where Most Deals Are Won — or Lost</h2>
          <p className="mkt-copy mkt-close-intro">
            The conversion layer is where money is made. Most real estate operations lose 60-70% of their potential deals here
            — not because of bad leads, but because of broken processes after the inquiry comes in. 78% of buyers work with the
            first agent who responds. (NAR, 2025)
          </p>
          <div className="mkt-grid mkt-grid-2 mkt-close-grid">
            <article className="mkt-close-column">
              <h3>Speed + Structure = Higher Closing</h3>
              <p>
                Leads contacted within 5 minutes are 21x more likely to be qualified than those contacted after 30 minutes.
                (MIT/InsideSales.com) Our automated system ensures no lead goes cold — ever.
              </p>
            </article>
            <article className="mkt-close-column">
              <h3>What We Set Up For You</h3>
              <ul className="mkt-list mkt-list-tight mkt-close-list">
                <li>Full CRM configuration and onboarding</li>
                <li>Automated lead routing and assignment</li>
                <li>Multi-touch follow-up sequences via WhatsApp, SMS, and email</li>
                <li>Pipeline dashboards with real-time visibility</li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      <section className="mkt-section">
        <div className="mkt-wrap mkt-trust-section">
          <p className="mkt-kicker mkt-section-kicker">COMPONENT 03</p>
          <h2>People Don't Buy Property. They Buy Trust.</h2>
          <p className="mkt-copy mkt-trust-intro">
            In high-ticket real estate, authority is the ultimate conversion lever. Buyers need to believe in your brand
            before they believe in your project. We build that credibility — systematically and strategically.
          </p>
          <div className="mkt-grid mkt-grid-3">
            {trustCards.map((item) => (
              <article className="mkt-card mkt-trust-card" key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mkt-section">
        <div className="mkt-wrap mkt-split mkt-case-study">
          <div className="mkt-case-copy">
            <h2>Leads Were Already There. Conversions Were Not.</h2>
            <p className="mkt-copy mkt-case-intro">
              One of our clients — a mid-sized developer in Karachi — was running consistent ad campaigns and generating 200+
              inquiries per month. Revenue, however, remained erratic. The problem wasn't awareness. It was everything that came after.
            </p>
            <h3 className="mkt-case-heading">The Diagnosis</h3>
            <div className="mkt-case-list">
              {caseStudyItems.map((item) => (
                <article className="mkt-case-item" key={item.title}>
                  <div className="mkt-case-arrow" aria-hidden="true">→</div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
          <img src={caseStudyImage} alt="Case study conversion rate chart" className="mkt-side-image mkt-case-image" />
        </div>
      </section>

      <section className="mkt-section">
        <div className="mkt-wrap mkt-dev-section">
          <h2>Built for Developers Who Want Predictable Sales</h2>
          <div className="mkt-grid mkt-grid-3">
            <article className="mkt-stat"><h3>Rs 50Cr+</h3><h4>Pipeline Generated</h4><p>Qualified sales pipeline built for residential and commercial segments.</p></article>
            <article className="mkt-stat"><h3>3x</h3><h4>Avg. Lead-to-Visit Lift</h4><p>Clients see faster movement from first inquiry to qualified site visit.</p></article>
            <article className="mkt-stat"><h3>90</h3><h4>Days to Full System</h4><p>From strategy call to fully operational sales system in ~90 days.</p></article>
          </div>
          <div className="mkt-grid mkt-grid-2 mkt-dev-quotes">
            <blockquote className="mkt-quote mkt-dev-quote">
              <p>“We went from chasing leads to having a pipeline we could actually predict.”</p>
              <cite>— Director of Sales, Karachi-based residential developer</cite>
            </blockquote>
            <blockquote className="mkt-quote mkt-dev-quote">
              <p>“Finally, a team that talks about systems, not just clicks and impressions.”</p>
              <cite>— CEO, Mid-tier real estate agency</cite>
            </blockquote>
          </div>
        </div>
      </section>

      <section className="mkt-section" id="how-it-works">
        <div className="mkt-wrap">
          <h2>How We Build Your Sales System</h2>
          <div className="mkt-grid mkt-grid-2">
            {processSteps.map((step) => (
              <article className="mkt-step" key={step.no}>
                <h3>{step.no} — {step.title}</h3>
                <p>{step.text}</p>
              </article>
            ))}
          </div>
          <p className="mkt-callout">We iterate and improve continuously, so each cycle compounds your advantage.</p>
        </div>
      </section>

      <section className="mkt-section">
        <div className="mkt-wrap mkt-growth-section">
          <p className="mkt-kicker mkt-section-kicker">INVESTMENT</p>
          <h2>Your Growth Plan</h2>
          <p className="mkt-copy mkt-growth-intro">
            Every engagement is custom-scoped based on your current stage, project pipeline, and revenue goals. We don't
            offer off-the-shelf packages — we build infrastructure that matches where you're going.
          </p>
          <div className="mkt-grid mkt-grid-3 mkt-growth-grid">
            {growthPlanCards.map((item, index) => (
              <article className="mkt-card mkt-growth-card" key={item.title}>
                <div className="mkt-growth-card-top">
                  <div className={`mkt-growth-icon mkt-growth-icon-${index + 1}`} aria-hidden="true" />
                </div>
                <div className="mkt-growth-card-body">
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              </article>
            ))}
          </div>
          <p className="mkt-callout mkt-growth-callout">
            Engagements begin with a <strong>Strategy & Audit Session</strong>. Investment is determined after we understand the full scope of your opportunity.
          </p>
        </div>
      </section>

      <section className="mkt-section">
        <div className="mkt-wrap mkt-inhouse-section">
          <h2>Why Not Just Hire In-House?</h2>
          <p className="mkt-copy mkt-inhouse-intro">
            It's a fair question. And if you have 6–12 months to recruit, onboard, and train a full growth team while
            running live projects — an in-house build might work. But most developers don't have that runway. Here's the real comparison.
          </p>
          <p className="mkt-callout mkt-inhouse-callout">
            <span className="mkt-inhouse-icon" aria-hidden="true" />
            <span>
              We bring tested systems, not experiments. Every process we implement has been validated across real projects
              — so your growth doesn't fund our learning curve.
            </span>
          </p>
        </div>
      </section>

      <section className="mkt-section">
        <div className="mkt-wrap mkt-daily-section">
          <h2>Let's Build a System That Works Every Day</h2>
          <p className="mkt-copy mkt-daily-intro">
            Not just on launch day. Not just when campaigns are fresh. Every day — through a repeatable, engineered process
            that generates qualified leads, follows up automatically, and converts with consistency.
          </p>
          <div className="mkt-grid mkt-grid-3 mkt-daily-grid">
            {dailySystemCards.map((item) => (
              <article className="mkt-card mkt-daily-card" key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
          <div className="mkt-btn-row mkt-daily-actions">
            <a className="mkt-link-btn" href="https://landtrack.pk" target="_blank" rel="noreferrer">Start Your 30-Day Setup</a>
            <a id="strategy-call" className="mkt-link-btn mkt-btn-outline" href="https://landtrack.pk" target="_blank" rel="noreferrer">Schedule a Strategy Call</a>
          </div>
          <p className="mkt-callout mkt-daily-callout">
            <span className="mkt-inhouse-icon" aria-hidden="true" />
            <span>
              <strong>Limited onboarding slots available each month.</strong> We work with a select number of clients to ensure
              every engagement receives full attention and delivers measurable results.
            </span>
          </p>
        </div>
      </section>

      <section className="mkt-section">
        <div className="mkt-wrap">
          <h2>The Bottom Line</h2>
          <div className="mkt-grid mkt-grid-3">
            <article className="mkt-card"><h3>Your leads are already out there.</h3><p>Demand exists. The missing piece is the infrastructure to capture, nurture, and close at scale.</p></article>
            <article className="mkt-card"><h3>More spend won't fix a broken system.</h3><p>Doubling ad budget into a leaking funnel doubles loss, not growth.</p></article>
            <article className="mkt-card"><h3>Predictability is a competitive edge.</h3><p>When you know your next 10, 20, or 50 deals, execution becomes a strategy not a gamble.</p></article>
          </div>
          <p className="mkt-tagline">Landtrack.pk doesn't run campaigns. We build the engine that makes every campaign compound.</p>
        </div>
      </section>

      <section className="mkt-section">
        <div className="mkt-wrap mkt-split mkt-cta-section">
          <div className="mkt-cta-copy">
            <h2 className="mkt-brand">Landtrack.pk</h2>
            <p className="mkt-copy mkt-cta-intro">
              Predictable Real Estate Sales Systems — built for developers, builders, and agencies who are done leaving revenue on the table.
            </p>
            <div className="mkt-cta-list">
              <article className="mkt-cta-item">
                <div className="mkt-cta-icon mkt-cta-icon-mail" aria-hidden="true" />
                <div>
                  <h3>Get in Touch</h3>
                  <p>
                    Reach out to begin your system audit and strategy session. We'll identify exactly where your pipeline is
                    leaking — and how to fix it.
                  </p>
                </div>
              </article>
              <article className="mkt-cta-item">
                <div className="mkt-cta-icon mkt-cta-icon-globe" aria-hidden="true" />
                <div>
                  <h3>landtrack.pk</h3>
                  <p>
                    Visit our website to explore case studies, service details, and the full framework behind how we build real
                    estate sales systems.
                  </p>
                </div>
              </article>
            </div>
            <div className="mkt-btn-row mkt-cta-actions">
              <a className="mkt-link-btn" href="https://landtrack.pk" target="_blank" rel="noreferrer">Visit Landtrack.pk</a>
              <a className="mkt-link-btn mkt-btn-outline" href="https://landtrack.pk" target="_blank" rel="noreferrer">Book a Free Strategy Call</a>
            </div>
          </div>
          <img src={ctaImage} alt="Landtrack city development view" className="mkt-side-image mkt-cta-image" />
        </div>
      </section>

      <section className="mkt-section mkt-testimonials">
        <div className="mkt-wrap mkt-testimonials-wrap">
          <h2>What Our Clients Say</h2>
          <p className="mkt-copy">Trusted by developers, builders, and real estate professionals.</p>
          <div className="mkt-grid mkt-grid-3 mkt-testimonial-grid">
            {testimonials.map((item, index) => (
              <blockquote key={item.by} className={`mkt-card mkt-testimonial-card mkt-testimonial-card-${index + 1}`}>
                <div className="mkt-testimonial-stars" aria-hidden="true">★★★★★</div>
                <p>{item.quote}</p>
                <cite>— {item.by}</cite>
              </blockquote>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default MarketingPage;
